import type { ReactNode } from 'react';
import { Button, Card, Space, Tooltip } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export type CollapsibleCardProps = {
    title: ReactNode;
    collapsed: boolean;
    onToggle: () => void;
    extra?: ReactNode;
    children?: ReactNode;
};

/**
 * Inner card with a collapse chevron in its header. Extracted from cellControllerTuning (LT-3348) so
 * the Diagnostics page shares it rather than copying it. Module-scope component so typing in a card's
 * inputs doesn't remount (which would drop focus). The body is hidden — not unmounted-then-remounted —
 * only when the user actually collapses it, so child state survives a collapse.
 *
 * The whole TITLE toggles, not only the chevron. A 24px chevron at the far right of a full-width card
 * is a small target for a frequent action; the title reads as the thing you'd click. `extra` is left
 * alone on purpose — it holds real buttons (Copy, Clear, Run) whose clicks must not also collapse the
 * card they sit in.
 */
export const CollapsibleCard = ({ title, collapsed, onToggle, extra, children }: CollapsibleCardProps): JSX.Element => {
    const { t } = useTranslation();
    const label = collapsed ? t('common.expand') : t('common.collapse');
    return (
        <Card
            type="inner"
            size="small"
            title={
                // display:block so the click target is the full title cell, not just the text glyphs.
                <span
                    role="button"
                    tabIndex={0}
                    aria-expanded={!collapsed}
                    aria-label={label}
                    onClick={onToggle}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
                    style={{ display: 'block', cursor: 'pointer', userSelect: 'none' }}
                >
                    {title}
                </span>
            }
            extra={
                <Space size="small">
                    {extra}
                    {/* Real Button so the toggle is keyboard-focusable and Enter/Space-operable. */}
                    <Tooltip title={label}>
                        <Button
                            type="text"
                            size="small"
                            aria-label={label}
                            aria-expanded={!collapsed}
                            icon={collapsed
                                ? <DownOutlined style={{ color: '#8a98a8' }} />
                                : <UpOutlined style={{ color: '#8a98a8' }} />}
                            onClick={onToggle}
                        />
                    </Tooltip>
                </Space>
            }
        >
            <div style={{ display: collapsed ? 'none' : 'block' }}>{children}</div>
        </Card>
    );
};

export default CollapsibleCard;
